import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    HStack,
    IconButton,
    Portal,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { HiPaperClip, HiXMark } from "react-icons/hi2";

const AddNoteModal = ({
    isOpen,
    onClose,
    noteContent,
    setNoteContent,
    noteFiles,
    setNoteFiles,
    onSubmit,
    isLoading,
}) => {
    const { t } = useTranslation("models");

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(e) => {
                if (!e.open) onClose();
            }}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content
                        bg="bg.card"
                        color="white"
                        borderColor="border.primary"
                        border="1px solid"
                        w={{ base: "calc(100vw - 2rem)", md: "lg" }}
                        maxW="100%"
                        maxH="calc(100dvh - 2rem)"
                        overflowY="auto"
                        borderRadius={12}
                    >
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight={700} fontSize="lg" mb={4}>{t("candidateDetailPage.addNoteModalTitle")}</Text>
                            <Textarea
                                placeholder={t("candidateDetailPage.noteContentPlaceholder")}
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                rows={5}
                                bg="bg.secondary"
                                border="1px solid"
                                borderColor="border.primary"
                                color="white"
                                _placeholder={{ color: "text.grey" }}
                                resize="vertical"
                                mb={4}
                            />
                            <Box mb={4}>
                                <Text fontSize="sm" color="text.grey" mb={2}>{t("candidateDetailPage.noteAttachmentsLabel")}</Text>
                                <Button
                                    as="label"
                                    htmlFor="note-file-input"
                                    variant="outlined"
                                    size="sm"
                                    cursor="pointer"
                                    mb={noteFiles.length > 0 ? 3 : 0}
                                >
                                    <HiPaperClip />
                                    {t("candidateDetailPage.noteAttachSelectFiles")}
                                    <input
                                        id="note-file-input"
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files ?? []);
                                            setNoteFiles(prev => [...prev, ...picked]);
                                            e.target.value = "";
                                        }}
                                    />
                                </Button>
                                {noteFiles.length > 0 && (
                                    <VStack align="stretch" gap={1}>
                                        {noteFiles.map((f, i) => (
                                            <HStack
                                                key={i}
                                                bg="bg.secondary"
                                                borderRadius={8}
                                                px={3}
                                                py={2}
                                                justify="space-between"
                                            >
                                                <HStack gap={2} minW={0}>
                                                    <Box color="text.grey" flexShrink={0}><HiPaperClip /></Box>
                                                    <Text fontSize="xs" color="white" fontFamily="mono" truncate>{f.name}</Text>
                                                </HStack>
                                                <IconButton
                                                    variant="ghost"
                                                    size="xs"
                                                    aria-label={t("candidateDetailPage.noteRemoveFile")}
                                                    color="text.grey"
                                                    flexShrink={0}
                                                    onClick={() => setNoteFiles(prev => prev.filter((_, j) => j !== i))}
                                                >
                                                    <HiXMark />
                                                </IconButton>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                            <HStack justify="flex-end" gap={2}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t("candidateDetailPage.cancelNote")}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    loading={isLoading}
                                    disabled={!noteContent.trim()}
                                    onClick={onSubmit}
                                >
                                    {t("candidateDetailPage.submitNote")}
                                </Button>
                            </HStack>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" color="text.grey" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default AddNoteModal;
